import type {
  ApolloCache,
  DefaultContext,
  FetchResult,
  MutationFunctionOptions,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { ApolloError } from '@apollo/client';
import { isNil } from 'es-toolkit';

import type { ResultOf, VariablesOf } from '@savvy/deprecated-shared/gql-compat';
import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { isWeb } from '@savvy/deprecated-shared/utils/platform/is-web';
import {
  SALESFORCE_ACCESS_TOKEN_EXPIRATION,
  SALESFORCE_ACCESS_TOKEN_KEY,
} from '@savvy/deprecated-shared/utils/salesforce';

export const ReAuthWithSalesforceMutation = tadaGraphql(/* GraphQL */ `
  mutation GenerateSalesforceAccessToken($input: GenerateSalesforceAccessTokenInput!) {
    generateSalesforceAccessToken(input: $input) {
      accessToken
    }
  }
`);

function redirectToSalesforceOAuth() {
  window.location.href = '/advisor/dashboard/auth';
}

type GenerateSalesforceAccessToken = (
  options?: MutationFunctionOptions<
    ResultOf<typeof ReAuthWithSalesforceMutation>,
    VariablesOf<typeof ReAuthWithSalesforceMutation>,
    DefaultContext,
    ApolloCache<unknown>
  >,
) => Promise<FetchResult<ResultOf<typeof ReAuthWithSalesforceMutation>>>;

export function reAuthWithSalesforce(generateSalesforceAccessToken: GenerateSalesforceAccessToken, shouldSync = true) {
  if (!shouldSync || !isWeb()) {
    return;
  }
  generateSalesforceAccessToken({
    variables: {
      input: {},
    },
  })
    .then(({ errors, data }) => {
      if (errors || isNil(data?.generateSalesforceAccessToken)) {
        console.error('Error fetching new access token: ', { errors });
        redirectToSalesforceOAuth();
      } else {
        setSalesforceAccessToken(data.generateSalesforceAccessToken.accessToken);
        window.location.reload();
      }
    })
    .catch((err: unknown) => {
      // Apollo wraps fetch failures in ApolloError with a non-null `networkError`.
      // Use that signal directly rather than matching browser-specific error
      // message strings ("NetworkError", "Load failed", etc).
      if (err instanceof ApolloError && err.networkError !== null) {
        window.location.reload();
      } else {
        console.error('Error fetching new access token: ', { err });
        redirectToSalesforceOAuth();
      }
    });
}

export function getSalesforceAccessToken() {
  if (!isWeb()) {
    return;
  }
  // In SSR there is no window object, so window.localStorage is not available
  return typeof window !== 'undefined' ? window.localStorage.getItem(SALESFORCE_ACCESS_TOKEN_KEY) : null;
}

export function removeSalesforceAccessToken() {
  if (!isWeb() || typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(SALESFORCE_ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(SALESFORCE_ACCESS_TOKEN_EXPIRATION);
}

export function setSalesforceAccessToken(accessToken: string) {
  if (!isWeb() || typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(SALESFORCE_ACCESS_TOKEN_KEY, accessToken);
  setSalesforceTokenExpiration();
}

function setSalesforceTokenExpiration() {
  if (typeof window === 'undefined') {
    return;
  }
  // Salesforce Access Tokens typically expire in 2 hours (7,200 seconds), but this value is not guaranteed to be static—Salesforce could change it at any time with no warning.
  const expirationTime = new Date().getTime() + 30 * 60 * 1000; // 30min in milliseconds

  window.localStorage.setItem(SALESFORCE_ACCESS_TOKEN_EXPIRATION, expirationTime.toString());
}

export function isServerError(error: Error | ServerParseError | ServerError): error is ServerError {
  return 'statusCode' in error;
}
